import React from 'react';
import { WORK_HOURS_PER_WEEK, ADMIN_HOURS } from '../../utils/utilizationCalc';

// ── Inline bar chart ──────────────────────────────────────────────────────────

function UtilBar({ pct, widthPx = 120 }) {
  const safe = Math.min(2, Math.max(0, Number.isFinite(pct) ? pct : 0)); // cap display at 200%
  const fill = Math.min(1, safe); // bar width capped at 100%
  const color =
    safe > 1.0 ? '#ef5350'     // over-booked → red
    : safe > 0.9 ? '#fb8c00'   // near-full → orange
    : safe > 0.0 ? '#1565c0'   // normal → blue
    : '#37474f';                // zero → muted
  return (
    <div
      style={{
        width: widthPx,
        height: 14,
        background: '#2e3f4a',
        borderRadius: 3,
        overflow: 'hidden',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    >
      <div
        style={{
          width: `${fill * 100}%`,
          height: '100%',
          background: color,
          borderRadius: 3,
          transition: 'width 0.3s ease',
        }}
      />
    </div>
  );
}

function SoftBar({ pct, widthPx = 120 }) {
  const safe = Math.min(2, Math.max(0, Number.isFinite(pct) ? pct : 0));
  const fill = Math.min(1, safe);
  return (
    <div
      style={{
        width: widthPx,
        height: 14,
        background: '#2e3f4a',
        borderRadius: 3,
        overflow: 'hidden',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    >
      <div
        style={{
          width: `${fill * 100}%`,
          height: '100%',
          background: safe > 0 ? '#00838f' : '#37474f',
          borderRadius: 3,
          transition: 'width 0.3s ease',
        }}
      />
    </div>
  );
}

function PtoBar({ ptoHours, widthPx = 120 }) {
  const pct = Math.min(1, ptoHours / WORK_HOURS_PER_WEEK);
  return (
    <div
      style={{
        width: widthPx,
        height: 14,
        background: '#2e3f4a',
        borderRadius: 3,
        overflow: 'hidden',
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    >
      <div
        style={{
          width: `${pct * 100}%`,
          height: '100%',
          background: '#7b1fa2',
          borderRadius: 3,
        }}
      />
    </div>
  );
}

// ── Stat cell helper ──────────────────────────────────────────────────────────

function StatCell({ label, hours, pct, bar, barWidth = 100 }) {
  const pctStr = Number.isFinite(pct) ? `${Math.round(pct * 100)}%` : '—';
  return (
    <td style={S.statCell}>
      <div style={S.statLabel}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {bar}
        <span style={S.pctText}>{pctStr}</span>
      </div>
      <div style={S.hoursText}>{Number.isFinite(hours) ? `${hours.toFixed(1)} h` : '—'}</div>
    </td>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  wrapper: { fontSize: 13, color: '#eceff1' },
  emptyMsg: { padding: '32px 16px', textAlign: 'center', color: '#78909c', fontSize: 14 },
  groupHeader: {
    padding: '8px 12px',
    background: '#1a3a4a',
    borderLeft: '4px solid #1565c0',
    fontWeight: 700,
    fontSize: 13,
    color: '#90caf9',
    marginTop: 16,
    marginBottom: 0,
    borderRadius: '4px 4px 0 0',
  },
  table: { borderCollapse: 'collapse', width: '100%', marginBottom: 0 },
  th: {
    padding: '7px 10px',
    background: '#263238',
    borderBottom: '2px solid #37474f',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 11,
    color: '#78909c',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '8px 10px',
    borderBottom: '1px solid #263238',
    verticalAlign: 'middle',
    background: '#1e2e36',
  },
  tdAlt: { background: '#1a2a32' },
  statCell: {
    padding: '8px 10px',
    borderBottom: '1px solid #263238',
    verticalAlign: 'top',
    background: 'inherit',
    minWidth: 150,
  },
  statLabel: { fontSize: 10, color: '#78909c', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 },
  pctText: { fontWeight: 700, fontSize: 14, color: '#e3f2fd', minWidth: 38 },
  hoursText: { fontSize: 11, color: '#90caf9', marginTop: 2 },
  nameCell: {
    padding: '8px 12px',
    borderBottom: '1px solid #263238',
    verticalAlign: 'middle',
    fontWeight: 600,
    minWidth: 160,
    background: 'inherit',
  },
  availCell: {
    padding: '8px 10px',
    borderBottom: '1px solid #263238',
    verticalAlign: 'middle',
    textAlign: 'right',
    background: 'inherit',
  },
};

// ── Main component ────────────────────────────────────────────────────────────

export default function PersonUtilizationView({ personRows = [], onAddResource, onAddSoftScheduleForPerson }) {
  if (personRows.length === 0) {
    return (
      <div style={S.emptyMsg}>No resources match these filters.</div>
    );
  }

  // Group by work type; blank/missing work type → "Unassigned Type"
  const groups = [];
  const groupMap = new Map();
  personRows.forEach(row => {
    const key = row.workType || '';
    const label = key || 'Unassigned Type';
    if (!groupMap.has(key)) {
      const g = { key, label, rows: [] };
      groups.push(g);
      groupMap.set(key, g);
    }
    groupMap.get(key).rows.push(row);
  });

  return (
    <div style={S.wrapper}>
      {onAddResource && (
        <div style={{ marginBottom: 12 }}>
          <button
            style={{
              padding: '6px 14px',
              background: '#1565c0',
              border: '1px solid #0d47a1',
              borderRadius: 5,
              color: '#fff',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
            onClick={onAddResource}
          >
            + Add Resource
          </button>
        </div>
      )}

      {groups.map(group => (
        <div key={group.key} style={{ marginBottom: 28 }}>
          <div style={S.groupHeader}>{group.label}</div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Resource</th>
                <th style={S.th}>Scheduled Utilization</th>
                <th style={S.th}>Completed Utilization</th>
                <th style={S.th}>PTO This Week</th>
                <th style={S.th}>Soft Scheduled</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Available Capacity</th>
              </tr>
            </thead>
            <tbody>
              {group.rows.map((row, idx) => {
                const rowBg = idx % 2 === 0 ? '#1e2e36' : '#1a2a32';
                return (
                  <tr key={row.memberIdentifier || row.memberName} style={{ background: rowBg }}>
                    {/* Name */}
                    <td style={{ ...S.nameCell, background: rowBg }}>
                      <div>{row.memberName || row.memberIdentifier || '(unknown)'}</div>
                      {row.memberIdentifier && (
                        <div style={{ fontSize: 11, color: '#78909c', marginTop: 1 }}>{row.memberIdentifier}</div>
                      )}
                      {onAddSoftScheduleForPerson && (
                        <button
                          style={{ marginTop: 5, padding: '2px 8px', background: '#1a3a3a', border: '1px solid #37474f', borderRadius: 4, color: '#80cbc4', cursor: 'pointer', fontSize: 11 }}
                          onClick={() => onAddSoftScheduleForPerson(row)}
                        >
                          + Soft Schedule
                        </button>
                      )}
                    </td>

                    {/* Scheduled Utilization */}
                    <StatCell
                      label="Scheduled"
                      hours={row.scheduledHours}
                      pct={row.scheduledUtilization}
                      bar={<UtilBar pct={row.scheduledUtilization} widthPx={100} />}
                    />

                    {/* Completed Utilization */}
                    <StatCell
                      label="Completed"
                      hours={row.actualHours}
                      pct={row.actualUtilization}
                      bar={<UtilBar pct={row.actualUtilization} widthPx={100} />}
                    />

                    {/* PTO */}
                    <td style={{ ...S.statCell, background: rowBg }}>
                      <div style={S.statLabel}>PTO</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <PtoBar ptoHours={row.ptoHours} widthPx={100} />
                        <span style={{ ...S.pctText, color: row.ptoHours > 0 ? '#ce93d8' : '#78909c' }}>
                          {row.ptoHours > 0 ? `${row.ptoHours.toFixed(0)} h` : '—'}
                        </span>
                      </div>
                      {row.ptoDays && row.ptoDays.length > 0 && (
                        <ul style={{ margin: '6px 0 0 0', padding: 0, listStyle: 'none' }}>
                          {row.ptoDays.map((d, i) => (
                            <li key={i} style={{ fontSize: 11, color: '#ce93d8', marginTop: 2, display: 'flex', gap: 6 }}>
                              <span style={{ minWidth: 70 }}>{d.dayName}</span>
                              <span style={{ color: '#b0bec5' }}>
                                {d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>

                    {/* Soft Scheduled */}
                    <td style={{ ...S.statCell, background: rowBg }}>
                      <div style={S.statLabel}>Soft Scheduled</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <SoftBar pct={row.softScheduledUtilization || 0} widthPx={100} />
                        <span style={{ ...S.pctText, color: (row.softScheduledHours || 0) > 0 ? '#80cbc4' : '#78909c' }}>
                          {(row.softScheduledHours || 0) > 0 ? `${row.softScheduledHours.toFixed(0)} h` : '—'}
                        </span>
                      </div>
                      {row.softSchedules && row.softSchedules.length > 0 && (
                        <ul style={{ margin: '6px 0 0 0', padding: 0, listStyle: 'none' }}>
                          {row.softSchedules.map((ss, i) => (
                            <li key={i} style={{ fontSize: 11, color: '#80cbc4', marginTop: 2, display: 'flex', gap: 6 }}>
                              <span style={{ color: '#b0bec5', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ss.label}</span>
                              <span style={{ whiteSpace: 'nowrap' }}>{ss.hrsForWeek.toFixed(1)}h</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>

                    {/* Available Capacity */}
                    <td style={{ ...S.availCell, background: rowBg }}>
                      <div style={{ fontSize: 11, color: '#78909c', marginBottom: 2 }}>
                        {WORK_HOURS_PER_WEEK}h − {ADMIN_HOURS}h admin
                        {row.ptoHours > 0 ? ` − ${row.ptoHours.toFixed(0)}h PTO` : ''}
                        {row.scheduledHours > 0 ? ` − ${row.scheduledHours.toFixed(0)}h sched` : ''}
                        {(row.softScheduledHours || 0) > 0 ? ` − ${row.softScheduledHours.toFixed(0)}h soft` : ''}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 16, color: row.availableCapacity > 0 ? '#66bb6a' : '#ef5350' }}>
                        {row.availableCapacity.toFixed(0)} h
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Group summary bar */}
          <div style={{
            background: '#1a2a32',
            borderTop: '1px solid #37474f',
            padding: '6px 12px',
            display: 'flex',
            gap: 24,
            fontSize: 12,
            color: '#78909c',
          }}>
            <span>
              <strong style={{ color: '#90caf9' }}>
                {group.rows.reduce((s, r) => s + r.scheduledHours, 0).toFixed(1)} h
              </strong>
              {' '}total scheduled
            </span>
            <span>
              <strong style={{ color: '#90caf9' }}>
                {group.rows.reduce((s, r) => s + r.actualHours, 0).toFixed(1)} h
              </strong>
              {' '}total completed
            </span>
            <span>
              Avg sched:{' '}
              <strong style={{ color: '#e3f2fd' }}>
                {group.rows.length > 0
                  ? Math.round(group.rows.reduce((s, r) => s + r.scheduledUtilization, 0) / group.rows.length * 100)
                  : 0}%
              </strong>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
